import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { apiRequest } from '@/services/api'
import { API_CONFIG } from '@/config/api'

interface User {
  id: string
  email: string
  role: 'superadmin'
  profile?: {
    firstName?: string
    lastName?: string
    pseudo?: string
    preferredLanguage?: string
  }
  pendingEmail?: string
  subscription?: {
    type: string
    status: string
    eventsCount: number
    purchasedEvents: any[]
  }
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  hasCheckedAuth: boolean
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  hasCheckedAuth: false,
}

/**
 * Get current user from session (via httpOnly cookie)
 */
export const getCurrentUser = createAsyncThunk('auth/getCurrentUser', async (_, { rejectWithValue }) => {
  try {
    const response = await apiRequest<{ user: User }>({
      method: 'GET',
      url: API_CONFIG.endpoints.auth.me,
    })

    if (!response.success || !response.data?.user) {
      return rejectWithValue(response.message || 'Invalid response')
    }

    const user = response.data.user

    // Verify superadmin role
    if (user.role !== 'superadmin') {
      return rejectWithValue('Not a superadmin user')
    }

    return user
  } catch (error: any) {
    return rejectWithValue(error.message || 'Failed to get user')
  }
})

/**
 * Login user
 */
export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await apiRequest<{ user: User }>({
        method: 'POST',
        url: API_CONFIG.endpoints.auth.login,
        data: credentials,
      })

      if (!response.success || !response.data?.user) {
        return rejectWithValue(response.message || 'Login failed')
      }

      const user = response.data.user

      // Verify superadmin role
      if (user.role !== 'superadmin') {
        return rejectWithValue('Not a superadmin user')
      }

      return user
    } catch (error: any) {
      return rejectWithValue(error.message || 'Login failed')
    }
  },
)

/**
 * Update user preferences (profile data)
 */
export const updatePreferences = createAsyncThunk(
  'auth/updatePreferences',
  async (preferences: {
    profile?: {
      firstName?: string
      lastName?: string
      pseudo?: string
    }
    email?: string
  }, { rejectWithValue }) => {
    try {
      const response = await apiRequest<{ preferences: any }>({
        method: 'PUT',
        url: '/users/me/preferences',
        data: preferences,
      })

      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to update preferences')
      }

      return response.data?.preferences || preferences
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update preferences')
    }
  }
)

/**
 * Logout user - calls backend to clear cookies
 */
export const logout = createAsyncThunk('auth/logout', async (_, { rejectWithValue }) => {
  try {
    await apiRequest({
      method: 'POST',
      url: '/auth/logout',
    })
  } catch (error: any) {
    // Even if logout fails on the server, we still want to clear local state
    // Don't reject so we can still clear Redux state
    console.error('Logout error:', error.message)
  }
})

/**
 * Auth slice for managing authentication state
 */
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload
      state.isAuthenticated = true
      state.isLoading = false
      state.error = null
      state.hasCheckedAuth = true // Critical: set to true so AuthInitializer doesn't re-dispatch
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.isLoading = false
    },
    logout: (state) => {
      state.user = null
      state.isAuthenticated = false
      state.error = null
      state.hasCheckedAuth = true // Keep hasCheckedAuth true after logout to prevent re-check
    },
    clearError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      // login
      .addCase(login.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(login.fulfilled, (state, action) => {
        state.user = action.payload
        state.isAuthenticated = true
        state.isLoading = false
        state.error = null
        state.hasCheckedAuth = true
      })
      .addCase(login.rejected, (state, action) => {
        state.user = null
        state.isAuthenticated = false
        state.isLoading = false
        state.error = action.payload as string
      })
      // getCurrentUser
      .addCase(getCurrentUser.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload
        state.isAuthenticated = true
        state.isLoading = false
        state.error = null
        state.hasCheckedAuth = true
      })
      .addCase(getCurrentUser.rejected, (state) => {
        state.user = null
        state.isAuthenticated = false
        state.isLoading = false
        state.hasCheckedAuth = true
        // Don't set error here - it's expected when user is not authenticated
      })
      // updatePreferences
      .addCase(updatePreferences.fulfilled, (state, action) => {
        if (state.user) {
          const preferences = action.payload
          // Update profile fields if they exist
          if (preferences.firstName !== undefined) {
            state.user.profile!.firstName = preferences.firstName
          }
          if (preferences.lastName !== undefined) {
            state.user.profile!.lastName = preferences.lastName
          }
          if (preferences.pseudo !== undefined) {
            state.user.profile!.pseudo = preferences.pseudo
          }
          if (preferences.preferredLanguage !== undefined) {
            state.user.profile!.preferredLanguage = preferences.preferredLanguage
          }
          // Update pendingEmail if it exists (email change request)
          if (preferences.pendingEmail !== undefined) {
            state.user.pendingEmail = preferences.pendingEmail
          }
        }
      })
      // logout
      .addCase(logout.fulfilled, (state) => {
        state.user = null
        state.isAuthenticated = false
        state.error = null
        state.hasCheckedAuth = true
      })
      .addCase(logout.rejected, (state) => {
        state.user = null
        state.isAuthenticated = false
        state.error = null
        state.hasCheckedAuth = true
      })
  },
})

export const { setUser, setLoading, setError, clearError } = authSlice.actions
export default authSlice.reducer

